import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ChevronRight, ChevronDown, Plus, GripVertical, PanelLeftClose, PanelLeft } from 'lucide-react';
import { Step } from './StepEditor';
import { cn } from '@/lib/utils';
import { ResizableHandle, ResizablePanel } from '@/components/ui/resizable';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface ContentDirectoryProps {
  steps: Step[];
  onStepClick: (tempId: string) => void;
  onAddStep: () => void;
  onAddSubstep: (parentTempId: string) => void;
  onStepsReorder: (steps: Step[]) => void;
}

// Sortable item components
const SortableMainStep = ({ step, index, isExpanded, onToggleExpand, onStepClick, onAddSubstep, t }: any) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: step.tempId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <div
        className={cn(
          "flex items-center gap-2 p-2 rounded-md cursor-pointer hover:bg-muted/50 transition-colors group",
        )}
        onClick={() => onStepClick(step.tempId)}
      >
        {step.substeps && step.substeps.length > 0 && (
          <button
            className="p-0.5 hover:bg-muted rounded flex-shrink-0"
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpand(step.tempId);
            }}
          >
            {isExpanded ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>
        )}
        <button
          className="cursor-grab active:cursor-grabbing touch-none flex-shrink-0"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4 text-muted-foreground" />
        </button>
        <span className="text-sm font-medium flex-1 break-words">
          {index + 1}. {step.title || t('editor.untitledStep')}
        </span>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 opacity-0 group-hover:opacity-100 flex-shrink-0"
          onClick={(e) => {
            e.stopPropagation();
            onAddSubstep(step.tempId);
          }}
          title={t('editor.addSubstep')}
        >
          <Plus className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
};

const SortableSubstep = ({ substep, onStepClick, t }: any) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: substep.tempId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <div
        className={cn(
          "flex items-center gap-2 p-2 rounded-md cursor-pointer hover:bg-muted/50 transition-colors group",
        )}
        onClick={() => onStepClick(substep.tempId)}
      >
        <button
          className="cursor-grab active:cursor-grabbing touch-none flex-shrink-0"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4 text-muted-foreground" />
        </button>
        <span className="text-sm text-muted-foreground flex-1 break-words">
          {substep.step_number}. {substep.title || t('editor.untitledStep')}
        </span>
      </div>
    </div>
  );
};

export const ContentDirectory = ({
  steps,
  onStepClick,
  onAddStep,
  onAddSubstep,
  onStepsReorder,
}: ContentDirectoryProps) => {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(false);
  const [expandedSteps, setExpandedSteps] = useState<Set<string>>(
    new Set(steps.map(s => s.tempId))
  );

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const toggleExpand = (tempId: string) => {
    setExpandedSteps(prev => {
      const newSet = new Set(prev);
      if (newSet.has(tempId)) {
        newSet.delete(tempId);
      } else {
        newSet.add(tempId);
      }
      return newSet;
    });
  };

  const flattenSteps = (steps: Step[]): Step[] => {
    const flattened: Step[] = [];
    steps.forEach(step => {
      flattened.push(step);
      if (step.substeps && step.substeps.length > 0) {
        flattened.push(...step.substeps);
      }
    });
    return flattened;
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const activeStep = steps.find(s => s.tempId === active.id) ||
                       steps.flatMap(s => s.substeps || []).find(s => s.tempId === active.id);
    const overStep = steps.find(s => s.tempId === over.id) ||
                     steps.flatMap(s => s.substeps || []).find(s => s.tempId === over.id);

    if (!activeStep || !overStep) return;

    // Check if we're moving a substep within its parent's substep list
    if (activeStep.parent_step_id && overStep.parent_step_id &&
        activeStep.parent_step_id === overStep.parent_step_id) {
      // Reorder substeps within the same parent
      const newSteps = steps.map(mainStep => {
        if (mainStep.tempId === activeStep.parent_step_id) {
          const substeps = mainStep.substeps || [];
          const oldIndex = substeps.findIndex(s => s.tempId === active.id);
          const newIndex = substeps.findIndex(s => s.tempId === over.id);

          if (oldIndex !== -1 && newIndex !== -1) {
            const reorderedSubsteps = arrayMove(substeps, oldIndex, newIndex);
            return {
              ...mainStep,
              substeps: reorderedSubsteps.map((substep, index) => ({
                ...substep,
                step_number: index + 1,
              })),
            };
          }
        }
        return mainStep;
      });

      onStepsReorder(newSteps);
      return;
    }

    // Original logic for main step reordering
    const flatSteps = flattenSteps(steps);
    const oldIndex = flatSteps.findIndex(s => s.tempId === active.id);
    const newIndex = flatSteps.findIndex(s => s.tempId === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    // Reconstruct the tree structure after moving
    const newFlatSteps = arrayMove(flatSteps, oldIndex, newIndex);
    const reconstructed = reconstructTree(newFlatSteps);
    onStepsReorder(reconstructed);
  };

  const reconstructTree = (flatSteps: Step[]): Step[] => {
    const tree: Step[] = [];
    const mainSteps = flatSteps.filter(s => !s.parent_step_id);

    mainSteps.forEach(mainStep => {
      // Match substeps by either tempId or id (id is used when loaded from DB)
      const substeps = flatSteps.filter(s =>
        s.parent_step_id === mainStep.tempId || s.parent_step_id === mainStep.id
      );

      tree.push({
        ...mainStep,
        substeps: substeps.length > 0 ? substeps : undefined,
      });
    });

    // Renumber steps
    return renumberSteps(tree);
  };

  const renumberSteps = (steps: Step[]): Step[] => {
    return steps.map((step, index) => {
      const newStep = {
        ...step,
        step_number: index + 1,
      };

      if (newStep.substeps && newStep.substeps.length > 0) {
        newStep.substeps = newStep.substeps.map((substep, subIndex) => ({
          ...substep,
          step_number: subIndex + 1,
        }));
      }

      return newStep;
    });
  };

  if (collapsed) {
    return (
      <>
        <ResizablePanel
          defaultSize={3}
          minSize={3}
          maxSize={3}
          className="border-r border-border bg-card flex flex-col items-center py-4"
        >
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setCollapsed(false)}
            title={t('contentDirectory.expand')}
          >
            <PanelLeft className="h-5 w-5" />
          </Button>
        </ResizablePanel>
        <ResizableHandle withHandle />
      </>
    );
  }

  return (
    <>
      <ResizablePanel
        defaultSize={20}
        minSize={15}
        maxSize={40}
        className="border-r border-border bg-card flex flex-col h-full"
      >
        <div className="p-4 border-b border-border flex items-center justify-between">
          <h3 className="font-semibold text-lg">{t('editor.contents')}</h3>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setCollapsed(true)}
            title={t('contentDirectory.collapse')}
          >
            <PanelLeftClose className="h-5 w-5" />
          </Button>
        </div>

        <ScrollArea className="flex-1">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={flattenSteps(steps).map(s => s.tempId)}
              strategy={verticalListSortingStrategy}
            >
              <div className="p-4 space-y-2">
                {steps.map((step, index) => (
                  <div key={step.tempId}>
                    <SortableMainStep
                      step={step}
                      index={index}
                      isExpanded={expandedSteps.has(step.tempId)}
                      onToggleExpand={toggleExpand}
                      onStepClick={onStepClick}
                      onAddSubstep={onAddSubstep}
                      t={t}
                    />

                    {/* Substeps */}
                    {step.substeps && expandedSteps.has(step.tempId) && (
                      <div className="ml-8 mt-1 space-y-1">
                        {step.substeps.map((substep) => (
                          <SortableSubstep
                            key={substep.tempId}
                            substep={substep}
                            onStepClick={onStepClick}
                            t={t}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </ScrollArea>

        <div className="p-4 border-t border-border">
          <Button
            variant="outline"
            className="w-full"
            onClick={onAddStep}
          >
            <Plus className="mr-2 h-4 w-4" />
            {t('editor.addStep')}
          </Button>
        </div>
      </ResizablePanel>
      <ResizableHandle withHandle />
    </>
  );
};
